export const EDITED_SIMILARITY_THRESHOLD = 0.98;

function normalizeForCompare(text) {
  return String(text || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function levenshtein(left, right) {
  let a = left;
  let b = right;
  if (a.length > b.length) {
    const swap = a;
    a = b;
    b = swap;
  }
  const prev = new Array(a.length + 1);
  const curr = new Array(a.length + 1);
  for (let i = 0; i <= a.length; i += 1) prev[i] = i;
  for (let j = 1; j <= b.length; j += 1) {
    curr[0] = j;
    for (let i = 1; i <= a.length; i += 1) {
      const cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
      curr[i] = Math.min(prev[i] + 1, curr[i - 1] + 1, prev[i - 1] + cost);
    }
    for (let i = 0; i <= a.length; i += 1) prev[i] = curr[i];
  }
  return prev[a.length];
}

/* eslint-disable no-bitwise */
function rotl(value, shift) {
  return ((value << shift) | (value >>> (32 - shift))) >>> 0;
}

function sha1Hex(message) {
  const bytes = Array.from(new TextEncoder().encode(message));
  const ml = bytes.length;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  const bitLenHi = Math.floor((ml * 8) / 0x100000000);
  const bitLenLo = (ml * 8) >>> 0;
  bytes.push((bitLenHi >>> 24) & 0xff);
  bytes.push((bitLenHi >>> 16) & 0xff);
  bytes.push((bitLenHi >>> 8) & 0xff);
  bytes.push(bitLenHi & 0xff);
  bytes.push((bitLenLo >>> 24) & 0xff);
  bytes.push((bitLenLo >>> 16) & 0xff);
  bytes.push((bitLenLo >>> 8) & 0xff);
  bytes.push(bitLenLo & 0xff);

  let h0 = 0x67452301;
  let h1 = 0xefcdab89;
  let h2 = 0x98badcfe;
  let h3 = 0x10325476;
  let h4 = 0xc3d2e1f0;
  const words = new Array(80);

  for (let offset = 0; offset < bytes.length; offset += 64) {
    for (let t = 0; t < 16; t += 1) {
      const i = offset + t * 4;
      words[t] =
        ((bytes[i] << 24) |
          (bytes[i + 1] << 16) |
          (bytes[i + 2] << 8) |
          bytes[i + 3]) >>>
        0;
    }
    for (let t = 16; t < 80; t += 1) {
      words[t] = rotl(
        words[t - 3] ^ words[t - 8] ^ words[t - 14] ^ words[t - 16],
        1
      );
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    for (let t = 0; t < 80; t += 1) {
      let f;
      let k;
      if (t < 20) {
        f = (b & c) | (~b & d);
        k = 0x5a827999;
      } else if (t < 40) {
        f = b ^ c ^ d;
        k = 0x6ed9eba1;
      } else if (t < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8f1bbcdc;
      } else {
        f = b ^ c ^ d;
        k = 0xca62c1d6;
      }
      const temp = (rotl(a, 5) + f + e + k + words[t]) >>> 0;
      e = d;
      d = c;
      c = rotl(b, 30);
      b = a;
      a = temp;
    }
    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }

  return [h0, h1, h2, h3, h4]
    .map(n => n.toString(16).padStart(8, '0'))
    .join('');
}
/* eslint-enable no-bitwise */

export function assistantIdFromConversation(conversation) {
  const active = conversation?.ai_agent_active_assistant?.id;
  if (active != null && active !== '') return Number(active);
  const stored = conversation?.additional_attributes?.ai_agent?.assistant_id;
  if (stored != null && stored !== '') return Number(stored);
  return null;
}

export function suggestionIdFromText(text) {
  return sha1Hex(String(text || ''));
}

export function textSimilarity(left, right) {
  const a = normalizeForCompare(left);
  const b = normalizeForCompare(right);
  if (a === b) return 1;
  if (!a.length || !b.length) return 0;
  const distance = levenshtein(a, b);
  const ratio = 1 - distance / Math.max(a.length, b.length);
  return Math.round(Math.max(0, Math.min(1, ratio)) * 10000) / 10000;
}

export function buildCaptainSuggestionAttribute({
  usedMeta,
  shownMeta,
  sentText,
} = {}) {
  if (usedMeta?.suggestion_id) {
    const similarity = textSimilarity(usedMeta.text, sentText);
    const payload = {
      suggestion_id: usedMeta.suggestion_id,
      used: true,
      similarity,
      edited: similarity < EDITED_SIMILARITY_THRESHOLD,
    };
    if (usedMeta.assistant_id != null && usedMeta.assistant_id !== '') {
      payload.assistant_id = Number(usedMeta.assistant_id);
    }
    return payload;
  }

  if (shownMeta?.suggestion_id) {
    const payload = {
      suggestion_id: shownMeta.suggestion_id,
      used: false,
    };
    if (shownMeta.assistant_id != null && shownMeta.assistant_id !== '') {
      payload.assistant_id = Number(shownMeta.assistant_id);
    }
    return payload;
  }

  return null;
}

export function buildSuggestionMeta(text, conversation) {
  const value = String(text || '');
  if (!value.trim()) return null;
  const assistantId = assistantIdFromConversation(conversation);
  const meta = {
    text: value,
    suggestion_id: suggestionIdFromText(value),
    generated_at: new Date().toISOString(),
  };
  if (assistantId != null) meta.assistant_id = assistantId;
  return meta;
}
